// List export (PARITY Q27, Shared/listExporter). Replaces the portal's
// `jsonexport(data, {})` + react-admin `downloadCSV(csv, 'data')` with the
// same columns and file name, without the dependency:
// - nested objects become dotted columns (`nested.y.z`);
// - arrays of primitives are joined with ';' (`t1;t2`);
// - null/undefined give an empty cell, booleans 'true'/'false';
// - empty objects and arrays add no column;
// - the header is the union of keys in first-seen order; rows joined by '\n'.
// Deviation: jsonexport splits an array of objects over extra CSV rows,
// which misaligns the record's later columns; we keep one record per row and
// join the values of each sub-column with ';' instead.

type Cells = Map<string, string[]>

function push(cells: Cells, key: string, value: string) {
  const list = cells.get(key)
  if (list) list.push(value)
  else cells.set(key, [value])
}

function flatten(value: unknown, key: string, cells: Cells): void {
  if (value instanceof Date) {
    push(cells, key, value.toLocaleDateString())
    return
  }
  if (Array.isArray(value)) {
    for (const item of value) flatten(item, key, cells)
    return
  }
  if (value !== null && typeof value === 'object') {
    for (const [prop, child] of Object.entries(value))
      flatten(child, key ? `${key}.${prop}` : prop, cells)
    return
  }
  switch (typeof value) {
    case 'string':
      push(cells, key, value)
      return
    case 'number':
    case 'bigint':
      push(cells, key, String(value))
      return
    case 'boolean':
      push(cells, key, value ? 'true' : 'false')
      return
    default:
      push(cells, key, '')
  }
}

/** RFC 4180: quote a field holding a comma, quote or line break; double inner quotes. */
export function csvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

export function toCsv(rows: readonly unknown[]): string {
  if (rows.length === 0) return ''
  const header: string[] = []
  const seen = new Set<string>()
  const flat = rows.map((row) => {
    const cells: Cells = new Map()
    flatten(row, '', cells)
    for (const key of cells.keys()) {
      if (!seen.has(key)) {
        seen.add(key)
        header.push(key)
      }
    }
    return cells
  })
  const lines = [header.map(csvField).join(',')]
  for (const cells of flat) {
    lines.push(header.map((key) => csvField(cells.get(key)?.join(';') ?? '')).join(','))
  }
  return lines.join('\n')
}

/** Download `rows` as `<filename>.csv` (the portal's file is `data.csv`). */
export function downloadCsv(rows: readonly unknown[], filename = 'data'): void {
  const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.style.display = 'none'
  link.href = url
  link.download = `${filename}.csv`
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => {
    URL.revokeObjectURL(url)
  }, 0)
}
